import {
	CheckCircle,
	Circle,
	ExternalLink,
	Eye,
	Plus,
	Trash2,
	Upload,
} from "lucide-react";
import React from "react";
import { useDocumentTemplates } from "../context/DocumentTemplateContext";
import { cn } from "../lib/utils";
import type { ChecklistItem } from "../types";
import { openOrDownloadUrl } from "../utils/documentUrl";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";

interface ChecklistItemProps {
	item: ChecklistItem;
	onToggle: () => void;
	walletId?: string;
	checklistKey?: string;
	onSubtaskToggle?: (field: "sent" | "received") => void;
	onDocumentUpload?: (file: File) => void;
	onDocumentDelete?: () => void; // NEW: Handler for deleting uploaded documents
	customAction?: React.ReactNode; // Custom action button (e.g., Start KYB)
	hideSubtasks?: boolean; // Hide subtasks for partner view
	disabled?: boolean; // Disable the checkbox
}

const ChecklistItemComponent: React.FC<ChecklistItemProps> = ({
	item,
	onToggle,
	onSubtaskToggle,
	onDocumentUpload,
	onDocumentDelete,
	customAction,
	hideSubtasks,
	disabled = false,
}) => {
	const fileInputRef = React.useRef<HTMLInputElement>(null);
	const { getTemplateByChecklistId } = useDocumentTemplates();

	// Get the centralized template for this checklist item
	const centralTemplate = getTemplateByChecklistId(item.id);

	// Helper to check if a URL is valid
	const isValidUrl = (url: string | undefined) => {
		return url && url.trim() !== "" && url !== "#" && url !== "undefined";
	};

	// Determine which link to show (priority: item.documentUrl > item.link > uploaded doc > central template)
	const effectiveLink =
		(isValidUrl(item.documentUrl) ? item.documentUrl : null) ||
		(isValidUrl(item.link) ? item.link : null) ||
		(isValidUrl(item.subtasks?.documentUrl)
			? item.subtasks?.documentUrl
			: null) ||
		(isValidUrl(centralTemplate?.url) ? centralTemplate?.url : null);

	// Check if document has been uploaded directly to this item
	const hasUploadedDocument = isValidUrl(item.documentUrl);

	const handleViewDocument = () => {
		if (!effectiveLink || effectiveLink === "#") return;
		openOrDownloadUrl(
			effectiveLink,
			item.subtasks?.documentName || item.label || "document",
		);
	};

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file && onDocumentUpload) {
			onDocumentUpload(file);
		}
		// Reset input
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	return (
		<div className="flex flex-col gap-1.5">
			<div
				className={cn(
					"flex items-center gap-3 rounded-xl border border-(--color-border) bg-(--color-card) px-4 py-3 transition-all hover:border-(--color-accent-border)",
					hasUploadedDocument && "border-[#00E6C3]/35 bg-[#00E6C3]/5",
				)}
			>
				<button
					type="button"
					onClick={onToggle}
					disabled={disabled}
					className={cn(
						"flex shrink-0 items-center justify-center",
						disabled && "cursor-not-allowed opacity-60",
						!disabled && "cursor-pointer",
					)}
				>
					{item.completed ? (
						<CheckCircle size={20} className="text-[#00E6C3]" />
					) : (
						<Circle size={20} className="text-[#9a9a9a] dark:text-gray-400" />
					)}
				</button>
				<div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
					<button
						type="button"
						onClick={disabled ? undefined : onToggle}
						disabled={disabled}
						className={cn(
							"text-left font-medium text-(--color-foreground) text-sm",
							item.completed && "text-(--color-muted-foreground) line-through",
						)}
					>
						{item.label}
					</button>
					{effectiveLink && !hasUploadedDocument && (
						<button
							type="button"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								handleViewDocument();
							}}
							className="flex items-center gap-1 text-(--color-accent) text-sm hover:underline"
						>
							<ExternalLink size={14} />
							View
						</button>
					)}
				</div>

				{/* Document upload/view/delete actions */}
				{onDocumentUpload && (
					<div className="ml-auto flex items-center gap-2">
						{!hasUploadedDocument ? (
							<>
								<input
									ref={fileInputRef}
									type="file"
									accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
									className="hidden"
									onChange={handleFileSelect}
								/>
								<Button
									type="button"
									variant="subtle"
									size="sm"
									onClick={() => fileInputRef.current?.click()}
								>
									<Upload size={14} />
									Upload
								</Button>
							</>
						) : (
							<>
								<Button
									type="button"
									variant="subtle"
									size="sm"
									onClick={() => {
										if (item.documentUrl) {
											openOrDownloadUrl(
												item.documentUrl,
												item.documentName || "document.pdf",
											);
										}
									}}
								>
									<Eye size={14} />
									View
								</Button>
								{onDocumentDelete && (
									<Button
										type="button"
										variant="destructive"
										size="sm"
										onClick={onDocumentDelete}
									>
										<Trash2 size={14} />
										Delete
									</Button>
								)}
							</>
						)}
					</div>
				)}

				{/* Custom action (e.g., Start/Resume KYB) */}
				{customAction && (
					<div className="ml-auto flex items-center">{customAction}</div>
				)}
			</div>
			{item.completed && item.completedBy && (
				<div className="text-(--color-muted-foreground) text-xs pl-4">
					Completed by {item.completedBy} on{" "}
					{item.completedAt
						? new Date(item.completedAt).toLocaleString()
						: "Unknown"}
				</div>
			)}

			{/* Document Subtasks */}
			{item.subtasks && onSubtaskToggle && !hideSubtasks && (
				<div className="ml-6 flex flex-col gap-2 border-(--color-border) border-l-2 pl-4">
					<div className="flex items-center gap-2">
						<Checkbox
							id={`${item.id}-sent`}
							checked={item.subtasks.sent}
							onCheckedChange={() => onSubtaskToggle("sent")}
						/>
						<Label
							htmlFor={`${item.id}-sent`}
							className="cursor-pointer font-normal text-sm"
						>
							Sent to Partner
							{item.subtasks.sent && item.subtasks.sentAt && (
								<span className="text-(--color-muted-foreground) text-xs">
									{" "}
									by {item.subtasks.sentBy} on{" "}
									{new Date(item.subtasks.sentAt).toLocaleDateString()}
								</span>
							)}
						</Label>
					</div>

					<div className="flex flex-col gap-2">
						<div className="flex items-center gap-2">
							<Checkbox
								id={`${item.id}-received`}
								checked={item.subtasks.received}
								onCheckedChange={() => onSubtaskToggle("received")}
							/>
							<Label
								htmlFor={`${item.id}-received`}
								className="cursor-pointer font-normal text-sm"
							>
								Received from Partner
								{item.subtasks.received && item.subtasks.receivedAt && (
									<span className="text-(--color-muted-foreground) text-xs">
										{" "}
										by {item.subtasks.receivedBy} on{" "}
										{new Date(item.subtasks.receivedAt).toLocaleDateString()}
									</span>
								)}
							</Label>
						</div>

						{onDocumentUpload && (
							<div className="flex flex-wrap items-center gap-2">
								<input
									ref={fileInputRef}
									type="file"
									accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
									className="hidden"
									onChange={handleFileSelect}
									id={`file-${item.id}`}
								/>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => fileInputRef.current?.click()}
								>
									<Plus size={14} />
									Upload Document
								</Button>

								{item.subtasks.documentName && (
									<div className="flex items-center gap-2">
										<span className="text-(--color-foreground) text-sm">
											{item.subtasks.documentName}
										</span>
										<Button
											type="button"
											variant="link"
											size="sm"
											onClick={() => {
												if (
													item.subtasks?.documentUrl &&
													item.subtasks?.documentName
												) {
													openOrDownloadUrl(
														item.subtasks.documentUrl,
														item.subtasks.documentName,
													);
												}
											}}
										>
											Download
										</Button>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default ChecklistItemComponent;
